import React, { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  User,
  CreditCard,
  LogOut,
  Store,
  Plug,
  Bell,
  BellRing,
  Users,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useSettingsContext } from "../../context/SettingsContext";
import { useNotifications } from "../../context/NotificationContext";
import { useTheme } from "../../context/ThemeContext";
import { userService } from "../../services/UserService";
import { useLoader } from "../../context/LoaderContext";
import { useToastContext } from "../../context/ToastContext";
import LogoutPopupModal from "../settings/LogoutPopupModal";
import NotificationPanel from "../notifications/NotificationPanel";
import { AppConfig } from "../../utils/Constants";
import AvatarWithBlur from "../common/AvatarWithBlur";

const TopNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const { setIsSettingsOpen, setActiveSettingsTab } = useSettingsContext();
  const { userProfile, logout } = useAuth();
  const { notificationUnreadCount } = useNotifications();
  const { theme } = useTheme();
  const [isLogoutPopupOpen, setIsLogoutPopupOpen] = useState(false);
  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();

  const unreadCount = notificationUnreadCount?.unreadCount ?? 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node) &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setIsProfileOpen(false);
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSettingsNavigation = (tab: string) => {
    setIsSettingsOpen(true);
    setActiveSettingsTab(tab);
    setIsProfileOpen(false);
  };

  const handleLogoutClick = () => {
    setIsProfileOpen(false);
    setIsLogoutPopupOpen(true);
  };

  const handleLogout = async () => {
    showLoader();
    try {
      await userService.logoutUser();
      logout();
      showToast("success", "Logout successful!");
      setTimeout(() => {
        navigate("/auth");
      }, 1000);
    } catch (error) {
      console.error("Logout failed:", error);
      showToast("error", "Failed to logout. Please try again.");
    } finally {
      hideLoader();
    }
  };

  const toggleNotificationPanel = () => {
    setIsNotificationOpen((prev) => !prev);
    setIsProfileOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 bg-background-paper/40 dark:bg-background-paperDark/40 backdrop-blur-2xl z-50 flex items-center justify-between px-8 border-b border-divider/20 dark:border-divider-dark/20">
        {/* Logo */}
        <button
          onClick={() => {
            if (location.pathname === "/") {
              return;
            }
            navigate("/");
          }}
          className="flex items-center gap-3 group transition-all duration-200 px-3 py-2 rounded-lg hover:bg-background-panel/50 dark:hover:bg-background-panelDark/50"
        >
          <div className="relative flex items-center">
            <div className="absolute inset-0 bg-secondary/10 dark:bg-secondary/20 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div
              className={`relative flex items-center justify-center rounded-lg p-2 transition-all duration-200 backdrop-blur-sm ${
                theme === "light"
                  ? "bg-background-paper/60 border border-divider/30 group-hover:border-divider/50 group-hover:bg-background-paper/80"
                  : "bg-background-paperDark/60 border border-divider-dark/30 group-hover:border-secondary/30 group-hover:bg-background-paperDark/80"
              }`}
            >
              <img
                src={
                  theme === "light"
                    ? "https://cdn.forgeworks.ai/assets/forgeworks-logo-black.png"
                    : AppConfig.LOGO_URL
                }
                alt={`${AppConfig.PRODUCT_NAME_SHORT} Logo`}
                className="w-7 h-7 object-contain transition-transform duration-200 group-hover:scale-105 relative z-10"
              />
            </div>
          </div>
          <div className="flex flex-col items-start relative z-10">
            <span
              className={`text-lg font-light tracking-tight leading-tight transition-colors duration-200 ${
                theme === "dark" ? "text-white" : "text-text-primary"
              }`}
            >
              {AppConfig.PRODUCT_NAME_SHORT}
            </span>
            <span
              className={`text-[10px] font-light tracking-wider uppercase leading-none mt-0.5 transition-colors duration-200 opacity-70 ${
                theme === "dark"
                  ? "text-text-secondaryDark"
                  : "text-text-secondary"
              }`}
            >
              TOOLS
            </span>
          </div>
        </button>

        {/* Right Side - Notifications & Profile */}
        <div className="flex items-center gap-3">
          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={toggleNotificationPanel}
              className="relative p-2.5 rounded-lg hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
            >
              {unreadCount > 0 ? (
                <BellRing className="w-5 h-5 text-text-primary dark:text-text-secondaryDark" />
              ) : (
                <Bell className="w-5 h-5 text-text-primary dark:text-text-secondaryDark" />
              )}
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-background-paper dark:border-background-paperDark"></span>
              )}
            </button>
            {isNotificationOpen && (
              <div className="absolute right-0 top-full mt-2">
                <NotificationPanel onClose={toggleNotificationPanel} />
              </div>
            )}
          </div>

          {/* Profile Dropdown */}
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-2 group transition-all duration-200"
            >
              <div className="relative flex items-center">
                <div className="absolute inset-0 bg-secondary/5 dark:bg-secondary/10 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                <div
                  className={`relative flex items-center justify-center rounded-lg p-1.5 transition-all duration-200 overflow-hidden ${
                    theme === "light"
                      ? "bg-background-panel border border-divider/50 group-hover:border-divider group-hover:bg-background-default"
                      : "bg-background-paperDark/50 border border-divider-dark/30 group-hover:border-secondary/30 group-hover:bg-background-paperDark/70"
                  }`}
                >
                  {userProfile?.avatar ? (
                    <AvatarWithBlur src={userProfile.avatar} alt="User Avatar" />
                  ) : (
                    <div className="w-7 h-7 rounded-md bg-background-panel dark:bg-secondary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-black dark:text-secondary" />
                    </div>
                  )}
                </div>
              </div>
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-64 z-[101] overflow-hidden origin-top-right shadow-md rounded-lg bg-background-paper dark:bg-background-paperDark border border-black/12 dark:border-divider-dark"
                onClick={(e) => e.stopPropagation()}
                style={{
                  animation:
                    "dropdownAppear 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards",
                }}
              >
                {/* Header section with user info */}
                <div className="px-5 py-4 border-b border-black/12 dark:border-divider-dark">
                  <div className="flex items-center gap-3">
                    <div className="relative flex items-center flex-shrink-0">
                      <div className="relative flex items-center justify-center rounded-lg p-1.5 bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-divider-dark/30 overflow-hidden">
                        {userProfile?.avatar ? (
                          <AvatarWithBlur src={userProfile.avatar} alt="User Avatar" className="w-9 h-9 object-cover rounded-md" />
                        ) : (
                          <div className="w-9 h-9 rounded-md bg-background-panel dark:bg-secondary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-black dark:text-secondary" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-black dark:text-white leading-tight">
                        {userProfile?.firstName} {userProfile?.lastName}
                      </p>
                      <p className="text-xs text-black dark:text-text-secondaryDark mt-0.5 truncate">
                        {userProfile?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu items */}
                <div className="p-2.5">
                  <button
                    type="button"
                    onClick={() => handleSettingsNavigation("profile")}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg flex items-center gap-3.5 group transition-all duration-200"
                  >
                    <div className="w-9 h-9 rounded-lg bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-white/15 flex items-center justify-center">
                      <User className="w-[18px] h-[18px] text-black dark:text-text-primaryDark" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-black dark:text-white font-medium leading-none">
                        Profile
                      </span>
                      <span className="text-xs text-black dark:text-text-secondaryDark mt-1 leading-none">
                        Manage your personal details
                      </span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSettingsNavigation("members")}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg flex items-center gap-3.5 group transition-all duration-200 mt-1"
                  >
                    <div className="w-9 h-9 rounded-lg bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-white/15 flex items-center justify-center">
                      <Users className="w-[18px] h-[18px] text-black dark:text-text-primaryDark" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-black dark:text-white font-medium leading-none">
                        Organization
                      </span>
                      <span className="text-xs text-black dark:text-text-secondaryDark mt-1 leading-none">
                        Manage team members
                      </span>
                    </div>
                  </button>

                  {/* Hidden - will be brought back in the future
                  <button
                    type="button"
                    onClick={() => handleSettingsNavigation("billing")}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg flex items-center gap-3.5 group transition-all duration-200 mt-1"
                  >
                    <div className="w-9 h-9 rounded-lg bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-white/15 flex items-center justify-center">
                      <CreditCard className="w-[18px] h-[18px] text-black dark:text-text-primaryDark" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-black dark:text-white font-medium leading-none">
                        Billing
                      </span>
                      <span className="text-xs text-black dark:text-text-secondaryDark mt-1 leading-none">
                        Manage subscription & payments
                      </span>
                    </div>
                  </button>
                  */}

                  <button
                    type="button"
                    onClick={() => handleSettingsNavigation("integrations")}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg flex items-center gap-3.5 group transition-all duration-200 mt-1"
                  >
                    <div className="w-9 h-9 rounded-lg bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-white/15 flex items-center justify-center">
                      <Plug className="w-[18px] h-[18px] text-black dark:text-text-primaryDark" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-black dark:text-white font-medium leading-none">
                        Connectors
                      </span>
                      <span className="text-xs text-black dark:text-text-secondaryDark mt-1 leading-none">
                        Connect HubSpot, Stripe & more
                      </span>
                    </div>
                  </button>

                  <div className="w-full h-px bg-black/12 dark:bg-divider-dark my-2"></div>

                  <button
                    type="button"
                    onClick={handleLogoutClick}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg flex items-center gap-3.5 group transition-all duration-200"
                  >
                    <div className="w-9 h-9 rounded-lg bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-white/15 flex items-center justify-center">
                      <LogOut className="w-[18px] h-[18px] text-black dark:text-red-500" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-black dark:text-white font-medium leading-none">
                        Logout
                      </span>
                      <span className="text-xs text-black dark:text-text-secondaryDark mt-1 leading-none">
                        Sign out of your account
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Logout Modal */}
      {isLogoutPopupOpen && (
        <LogoutPopupModal
          onConfirm={handleLogout}
          onCancel={() => setIsLogoutPopupOpen(false)}
        />
      )}
    </>
  );
};

export default TopNav;
